import React, { useEffect, useRef, useState } from 'react'
import { useMutation } from '@apollo/client'
import { FormInstance, message } from 'antd'
import { UPDATE_LEGAL_FORMALITY_PARAMS_MUTATION } from 'graphql/LegalFormalities'

export type SaveStatus = 'idle' | 'saving' | 'saved'

// Saves each field on its own as soon as it changes: selects, toggles and
// date pickers on change, free text inputs on blur (or Enter).
// Only the changed field is sent, so concurrent saves never overwrite
// each other; saves are queued to keep them in order.
const useAutoSave = <T extends Record<string, string>>({
  form,
  legalFormalityId,
  initialValues,
  textFields = [],
}: {
  form: FormInstance<T>
  legalFormalityId: number
  initialValues: T
  textFields?: (keyof T & string)[]
}) => {
  const [updateParams] = useMutation(UPDATE_LEGAL_FORMALITY_PARAMS_MUTATION)
  const [status, setStatus] = useState<SaveStatus>('idle')
  // Last value asked for each field (saved or being saved)
  const savedValues = useRef<T>(initialValues)
  // Last value known to be stored on the server: restored when a save fails
  const serverValues = useRef<T>(initialValues)
  // Id of the latest save of each field still in flight
  const inFlight = useRef<Record<string, number>>({})
  const lastSaveId = useRef(0)
  const queue = useRef<Promise<void>>(Promise.resolve())
  const pending = useRef(0)
  const idleTimer = useRef<ReturnType<typeof setTimeout>>()

  // Server data can change at any time (other queries share the Apollo cache
  // entry): only fields changed on the server are written back, so a text
  // being typed is kept, and fields being saved wait for their own response.
  useEffect(() => {
    Object.entries(initialValues).forEach(([name, rawValue]) => {
      if (name in inFlight.current) return
      const value = rawValue ?? ''
      serverValues.current = { ...serverValues.current, [name]: value }
      const unchanged = value === (savedValues.current[name] ?? '')
      if (unchanged && form.getFieldValue(name) !== undefined) return
      savedValues.current = { ...savedValues.current, [name]: value }
      form.setFieldValue(name, value)
    })
  }, [form, JSON.stringify(initialValues)])

  useEffect(() => () => clearTimeout(idleTimer.current), [])

  const saveField = (name: keyof T & string, rawValue: unknown) => {
    const value = rawValue ? String(rawValue) : ''
    if (value === (savedValues.current[name] ?? '')) return

    savedValues.current = { ...savedValues.current, [name]: value }
    const saveId = ++lastSaveId.current
    inFlight.current[name] = saveId
    pending.current += 1
    setStatus('saving')
    clearTimeout(idleTimer.current)

    queue.current = queue.current.then(() =>
      updateParams({ variables: { id: legalFormalityId, [name]: value } })
        .then(({ data }) => {
          const errors: string[] =
            data?.updateLegalFormalityParams?.errors ?? []
          if (errors.length) throw new Error(errors.join(', '))
          serverValues.current = { ...serverValues.current, [name]: value }
        })
        .catch((e: Error) => {
          message.error(`Enregistrement impossible : ${e.message}`)
          // A newer change of this field is queued: it decides the value
          if (inFlight.current[name] !== saveId) return
          const restored = serverValues.current[name] ?? ''
          savedValues.current = { ...savedValues.current, [name]: restored }
          form.setFieldValue(name, restored)
        })
        .finally(() => {
          if (inFlight.current[name] === saveId) delete inFlight.current[name]
          pending.current -= 1
          if (pending.current > 0) return
          setStatus('saved')
          idleTimer.current = setTimeout(() => setStatus('idle'), 2000)
        })
    )
  }

  const onValuesChange = (changed: Partial<T>) => {
    Object.entries(changed).forEach(([name, value]) => {
      if (!textFields.includes(name)) saveField(name, value)
    })
  }

  const textFieldProps = (name: keyof T & string) => ({
    onBlur: () => saveField(name, form.getFieldValue(name)),
    onKeyDown: (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === 'Enter') {
        e.preventDefault()
        e.currentTarget.blur()
      }
    },
  })

  return { status, onValuesChange, textFieldProps }
}

export default useAutoSave
